<template>
  <div class="flipHome pane agendaSidebar" :class="[meetingStore.isScheduled ? 'scheduled' : 'quick']">
    <flipchart-content />

    <lobby-sidebar-schedule key="timeInfo" v-if="meetingStore.isScheduled" />

    <button
      v-if="isLobby"
      id="startMeeting"
      ref="start"
      class="enter startButton btn btn-solid btn-loud-green btn-blue-flood"
      :class="startClass"
      @click="requestStart"
    >
      <span v-if="!showLoading">Start Meeting</span>
      <span v-if="showLoading">Loading </span>
      <app-loading-spinner v-if="showLoading" class="spinner" />
    </button>
  </div>
</template>

<script setup>
import { ref, computed, watch, getCurrentInstance, nextTick, onMounted } from 'vue';
import _ from 'underscore';
import { storeToRefs } from 'pinia';
import { useMeetingStore } from '@/store/pinia/meeting';
import { useMessagesStore } from '@/store/pinia/messages';
import { usePhaseStore } from '@/store/pinia/phase';
import { useUsersStore } from '@/store/pinia/users';
import FlipchartContent from '@/components/FlipchartContent.vue';
import AppLoadingSpinner from '@/components/AppLoadingSpinner';
import LobbySidebarSchedule from './LobbySidebarSchedule.vue';
import utils from '../resources/utils';
import { useReady } from '@/composables/useReady';

const meetingStore = useMeetingStore();
const messagesStore = useMessagesStore();
const phaseStore = usePhaseStore();
const usersStore = useUsersStore();
const { $meetingmanager } = getCurrentInstance().appContext.config.globalProperties;
const { finishedFirstPhase } = useReady();

const flashHeyStart = ref(false);
const isLobby = ref(true);
const debouncedIsLobbyCheck = _.debounce(isLobbyCheck, 500);
const showLoading = ref(false);

const { currentPhase } = storeToRefs(phaseStore);
const { owner, iAmModerator, usersOnline, secondUserTime, haveModeratorsJoined } = storeToRefs(usersStore);
const { isRoom } = storeToRefs(meetingStore);

onMounted(() => {
  nextTick(() => {
    isLobby.value = currentPhase.value === 'lobby';
  });
});

const isLobbyRaw = computed(() => currentPhase.value === 'lobby');
const startClass = computed(() => {
  if (secondUserTime.value > 300) {
    return 'seriouslyHey';
  } else {
    return flashHeyStart.value ? 'hey' : '';
  }
});
const shouldFlash = computed(() => !isRoom.value || iAmModerator.value);

watch(currentPhase, (nv) => {
  if (nv !== 'lobby') {
    flashHeyStart.value = false;
    showLoading.value = false;
  }
  debouncedIsLobbyCheck(nv === 'lobby');
});
watch(isLobbyRaw, debouncedIsLobbyCheck, { immediate: true });
watch(finishedFirstPhase, (nv) => {
  if (!nv) {
    return false;
  }
  if (shouldFlash.value && currentPhase.value === 'lobby') {
    _.delay(() => {
      if (currentPhase.value === 'lobby') {
        heyStart();
      }
    }, 60000);
  }
});

function requestStart() {
  if (meetingStore.isRoom && !iAmModerator.value) {
    messagesStore.addAlert('pleaseWaitForRoomOwner', { ownerName: utils.desanitizeString(owner.value.first_name) });
  } else if (!haveModeratorsJoined.value) {
    messagesStore.addAlert('pleaseWaitForOwner', { ownerName: utils.desanitizeString(owner.value.first_name) });
  } else if (usersOnline.value.length < 2) {
    messagesStore.addAlert('pleaseWaitForUsers');
  } else {
    let check = messagesStore.interrupt('sureYouWantToStartMeeting', {});
    check
      .then(() => {
        if (phaseStore.currentPhase === 'lobby') {
          $meetingmanager.startMeeting();
          showLoading.value = true;
        }
      })
      .catch(() => {});
  }
}

function heyStart() {
  flashHeyStart.value = true;
}

function isLobbyCheck(nv) {
  isLobby.value = nv;
}
</script>
<style lang="scss" scoped>
.lobbyTimerHolder {
  margin: auto;
  width: 100%;
}

.spinner {
  margin-left: 1em;
}

.startButton {
  position: absolute;
  bottom: rem(30px);
  left: rem(20px);
  right: rem(20px);
  width: auto;
  height: rem(50px);
  &.hey {
    animation: please-start 8s infinite linear;
    transition: all 0s;

    @include triggered {
      animation: none;
    }
  }

  &.seriouslyHey {
    animation: seriously-please-start 2.5s infinite linear;
    transition: all 0s;

    @include triggered {
      animation: none;
    }
  }
}

.spacer {
  height: rem(150px);
  flex: 0 0 rem(150px);
  width: 100%;
  display: block;
}

.scheduled .spacer {
  height: rem(230px);
  flex: 0 0 rem(230px);
}
</style>
